/**
 * Route access rules for URLs not represented as nav leaves (detail tabs, extra
 * hackathon stats pages, labs, nested admin/email/signed-forms paths, etc.).
 */
import {
  getHackathonStudentStatsPath,
  getHackathonVolunteerStatsPath,
  getCreateEmailTemplatePath,
  getEmailTemplateDetailsPath,
  getRosieUserDetailsPath,
  getCreateRosieUserPath,
  getActivityLogPath,
  getSignedFormsCreateTemplatePath,
  getSignedFormsTemplatesPath,
  getFormRecipientsPath,
  getAutomatedCallsCreateBatchCallPath,
  getSurveyCreatePath,
  getSurveysListPath,
  getSclaAttendancePath,
  getSclaChallengeCompletionPath,
  getSclaCohortTeamBuilderPath,
  getStudentActivitiesAssignmentsListPath,
} from 'utils/navigation';

export type SupplementalRouteRuleSeed = { id: string; pathPrefix: string };

const PLACEHOLDER_USER = '000000000000000000000000';
const PLACEHOLDER_TEMPLATE = 'tpl';
const PLACEHOLDER_YEAR = '2026';

function pathOnly(href: string): string {
  const q = href.split('?')[0] ?? href;
  return q.split('#')[0] ?? q;
}

export function getSupplementalRouteRuleSeeds(): SupplementalRouteRuleSeed[] {
  return [
    { id: 'txters-details', pathPrefix: '/my-txt/profile' },
    // my-txt user detail tabs share the /my-txt/profile prefix (above covers routing);
    // this rule keeps parity with the backend ROUTE_RULE_IDS constant.
    { id: 'profiles-my-txt-user-detail', pathPrefix: '/my-txt/profile' },
    {
      id: 'other-txt-forms-templates',
      pathPrefix: pathOnly(getSignedFormsTemplatesPath()),
    },
    {
      id: 'other-txt-forms-recipients',
      pathPrefix: pathOnly(getFormRecipientsPath()),
    },
    {
      id: 'programs-scla-cohorts-attendance',
      pathPrefix: pathOnly(getSclaAttendancePath(PLACEHOLDER_YEAR)),
    },
    {
      id: 'programs-scla-cohorts-challenge-completion',
      pathPrefix: pathOnly(
        getSclaChallengeCompletionPath(PLACEHOLDER_YEAR),
      ),
    },
    {
      id: 'programs-scla-cohorts-teams',
      pathPrefix: pathOnly(getSclaCohortTeamBuilderPath(PLACEHOLDER_YEAR)),
    },
    {
      id: 'tools-txt-learning-list',
      pathPrefix: '/txt-learning/pages',
    },
    {
      id: 'tools-txt-learning-assignments',
      pathPrefix: pathOnly(getStudentActivitiesAssignmentsListPath()),
    },
    {
      id: 'programs-hnc-hackathon-student-stats',
      pathPrefix: pathOnly(getHackathonStudentStatsPath()),
    },
    {
      id: 'programs-hnc-hackathon-volunteer-stats',
      pathPrefix: pathOnly(getHackathonVolunteerStatsPath()),
    },
    {
      id: 'programs-hnc-hackathon-profiles',
      pathPrefix: '/hackathon/profile',
    },
    {
      id: 'programs-scla-staff',
      pathPrefix: '/bagel/staff',
    },
    {
      id: 'bagel-standards',
      pathPrefix: '/bagel/standards',
    },
    { id: 'labs-apprentice', pathPrefix: '/labs' },
    { id: 'labs-business', pathPrefix: '/labs/business' },
    { id: 'labs-business-cards', pathPrefix: '/labs/business-cards' },
    {
      id: 'other-txt-forms-templates-create',
      pathPrefix: pathOnly(getSignedFormsCreateTemplatePath()),
    },
    {
      id: 'tools-automated-calls-batch-create',
      pathPrefix: pathOnly(getAutomatedCallsCreateBatchCallPath()),
    },
    // Surveys UI/routes commented out in nav + protected routes; keep rule seeds
    // so ROUTE_RULE_IDS parity with txt-core-backend still holds.
    // NOTE: IDs were renamed (numbers stripped) — txt-core-backend ROUTE_RULE_IDS
    // and any stored user permissions must be migrated to match. See:
    // TXT Apps Permissions/context/2026-06-17/permission-id-migration.md
    {
      id: 'tools-surveys-list',
      pathPrefix: pathOnly(getSurveysListPath()),
    },
    {
      id: 'tools-surveys-create',
      pathPrefix: pathOnly(getSurveyCreatePath()),
    },
    {
      id: 'admin-tools-emails-template-create',
      pathPrefix: pathOnly(getCreateEmailTemplatePath()),
    },
    {
      id: 'admin-tools-emails-template-detail',
      pathPrefix: pathOnly(
        getEmailTemplateDetailsPath(PLACEHOLDER_TEMPLATE),
      ).replace(`/${PLACEHOLDER_TEMPLATE}`, ''),
    },
    {
      id: 'admin-tools-rosie-user-new',
      pathPrefix: pathOnly(getCreateRosieUserPath()),
    },
    {
      id: 'admin-tools-rosie-user-detail',
      pathPrefix: pathOnly(
        getRosieUserDetailsPath(PLACEHOLDER_USER),
      ).replace(`/${PLACEHOLDER_USER}`, ''),
    },
    {
      id: 'admin-tools-activity-logs',
      pathPrefix: pathOnly(getActivityLogPath()),
    },
    {
      id: 'admin-tools-manage-roles',
      pathPrefix: '/admin/manage-roles',
    },
  ];
}
